"use client";

import React, { useEffect, useState } from "react";
import { Search, Menu, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function SearchProductsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="224742" className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="flex items-center gap-4">
            <div className="size-16 shrink-0 animate-pulse rounded-md bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
              <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="py-4 text-center text-sm text-destructive">
        Si è verificato un errore durante la ricerca.
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-8 text-center text-sm font-medium text-muted-foreground">
        Nessun prodotto trovato.
      </div>;
  }
  return <div className="space-y-4">
      {products.map((product, i) => <div key={product.id} data-index={i} className="group flex items-center gap-4 rounded-lg p-2 transition-colors hover:bg-accent/50">
          {product.featured_image_url ? <div className="size-16 shrink-0 overflow-hidden rounded-md bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="size-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
            </div> : <div className="flex size-16 shrink-0 items-center justify-center rounded-md bg-muted">
              <Search className="size-6 text-muted-foreground/50" />
            </div>}
          <div className="min-w-0 flex-1">
            <Link to={product.link} className="block truncate text-sm font-medium transition-colors hover:text-primary">
              {product.title}
            </Link>
            <div className="mt-1 text-sm font-semibold">
              {product.price !== undefined ? `${product.price.toFixed(2)} €` : ""}
            </div>
          </div>
          <div className="w-24 shrink-0">
            <AddToCartButton productId={product.id} variant="secondary" size="sm" className="w-full" buttonClassName="bg-primary text-primary-foreground h-8 text-xs w-full font-bold uppercase tracking-tight" text="Aggiungi" showQuantity={false} />
          </div>
        </div>)}
    </div>;
}
function SearchOverlay() {
  const [search, setSearch] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(search);
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground transition-colors hover:text-primary hover:bg-accent/50" aria-label="Cerca Prodotti">
          <Search className="size-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] overflow-hidden border-border/50 bg-background/95 p-0 shadow-2xl backdrop-blur-xl sm:w-[450px]" align="end" sideOffset={16}>
        <div className="border-b border-border/50 p-4">
          <ProductsSearch value={search} onChange={setSearch} placeholder="Cerca e-bike o componenti..." className="w-full" />
        </div>
        {hasSearchQuery && <div className="max-h-[60vh] overflow-y-auto p-4">
            <EcommerceProductsProvider wp_query={wp_query}>
              <SearchProductsConsumer />
            </EcommerceProductsProvider>
          </div>}
      </PopoverContent>
    </Popover>;
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-5 w-24 animate-pulse rounded bg-muted/50" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default text-xs font-bold uppercase tracking-widest text-foreground/80 transition-colors hover:text-primary">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="mt-8 flex flex-col gap-6">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-8 w-full animate-pulse rounded bg-muted/50" />)}
      </div>;
  }
  return <nav className="mt-8 flex flex-col gap-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="border-b border-border/50 pb-4 font-default text-lg font-bold uppercase tracking-widest text-foreground/80 transition-colors hover:text-primary">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 text-foreground", isScrolled ? "border-b border-border/50 bg-background/80 shadow-md backdrop-blur-xl" : "bg-background")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        <div className="shrink-0">
          <Link to="/">
            <img src="/img/orma_rent_logo_header.png" alt="Orma Rent" className="h-8 w-auto brightness-0 dark:brightness-100" />
          </Link>
        </div>

        <div className="hidden flex-1 justify-center md:flex">
          <MenuProvider menu_id="50">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex shrink-0 items-center gap-2 md:gap-4">
          <SearchOverlay />
          <Link to="https://wa.me/" newTab>
            <Button variant="ghost" size="icon" className="text-foreground transition-colors hover:text-primary hover:bg-accent/50" aria-label="Contattaci su WhatsApp">
              <MessageCircle className="size-5" />
            </Button>
          </Link>
          <CartIcon variant="ghost" size="icon" className="text-foreground transition-colors hover:text-primary hover:bg-accent/50" iconSize="size-5" />

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-foreground transition-colors hover:text-primary hover:bg-accent/50" aria-label="Apri Menu">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] border-l-border/50 bg-background/95 backdrop-blur-xl sm:w-[400px]">
                <SheetTitle className="sr-only">Menu Mobile</SheetTitle>
                <div className="mt-8">
                  <Link to="/">
                    <img src="/img/orma_rent_logo_mobile.png" alt="Orma Rent" className="mb-12 h-8 w-auto brightness-0 dark:brightness-100" />
                  </Link>
                  <MenuProvider menu_id="50">
                    <MobileMenuConsumer />
                  </MenuProvider>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}